import React, { useCallback, useMemo, useState } from 'react';

import { useHistory, useParams } from 'react-router-dom';

import { AutoAwesome as AiIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardMedia,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Stack,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  DataGridPro,
  type GridColDef,
  type GridPaginationModel,
  type GridRenderCellParams,
} from '@mui/x-data-grid-pro';

import PageHeader from '~/components/Common/Elements/PageHeader';

import {
  useGetAttraction,
  useGetAttractionImages,
  useGetAttractionOffers,
  useResetAttractionImage,
  useUpdateAttractionImage,
} from '~/hooks/apis/attractions';

import { type AttractionImage, type MatchedOffer, formatAttractionType } from '~/types/attractionTypes';

const DEFAULT_PAGE_SIZE = 25;

const MATCH_TYPE_LABELS: Record<string, string> = {
  DIRECT_TITLE: 'Direct Title',
  AI_SEMANTIC: 'AI Semantic',
  PROXIMITY: 'Proximity',
  KEYWORD: 'Keyword',
};

const MATCH_TYPE_COLORS: Record<string, 'default' | 'success' | 'info' | 'warning' | 'primary'> = {
  DIRECT_TITLE: 'success',
  AI_SEMANTIC: 'primary',
  PROXIMITY: 'info',
  KEYWORD: 'warning',
};

export default function AttractionDetailPage() {
  const theme = useTheme();
  const themeColor = theme.palette.primary.main;
  const history = useHistory();
  const { attractionId } = useParams<{ attractionId: string }>();

  // Offers pagination
  const [offersPaginationModel, setOffersPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  // Image picker dialog
  const [isImagePickerOpen, setIsImagePickerOpen] = useState(false);

  // Data hooks
  const { data: attraction, isLoading: isAttractionLoading } = useGetAttraction(attractionId);

  const offersParams = useMemo(
    () => ({
      limit: offersPaginationModel.pageSize,
      offset: offersPaginationModel.page * offersPaginationModel.pageSize,
    }),
    [offersPaginationModel],
  );

  const {
    data: offersData,
    isLoading: isOffersLoading,
    error: offersError,
  } = useGetAttractionOffers(attractionId, offersParams);
  const offers: Array<MatchedOffer> = offersData?.result ?? [];
  const offersTotal = offersData?.total ?? 0;

  const {
    data: imagesData,
    isLoading: isImagesLoading,
    error: imagesError,
  } = useGetAttractionImages(attractionId, {
    enabled: isImagePickerOpen,
  });
  const images: Array<AttractionImage> = imagesData?.result ?? [];

  // Mutations
  const setImageMutation = useUpdateAttractionImage();
  const resetImageMutation = useResetAttractionImage();

  const handleOffersPaginationChange = useCallback((model: GridPaginationModel) => {
    setOffersPaginationModel((prev) => {
      if (prev.page === model.page && prev.pageSize === model.pageSize) {
        return prev;
      }
      return model;
    });
  }, []);

  const handleSetImage = useCallback(
    (imageId: string, imageUrl: string) => {
      setImageMutation.mutate({ attractionId, imageId, imageUrl }, { onSuccess: () => setIsImagePickerOpen(false) });
    },
    [attractionId, setImageMutation],
  );

  const handleResetImage = useCallback(() => {
    resetImageMutation.mutate(attractionId, {
      onSuccess: () => setIsImagePickerOpen(false),
    });
  }, [attractionId, resetImageMutation]);

  const offerColumns: Array<GridColDef<MatchedOffer>> = useMemo(
    () => [
      {
        field: 'title',
        headerName: 'Title',
        flex: 1,
        minWidth: 250,
      },
      {
        field: 'provider',
        headerName: 'Provider',
        width: 120,
        align: 'center',
        headerAlign: 'center',
      },
      {
        field: 'matchType',
        headerName: 'Match Type',
        width: 140,
        align: 'center',
        headerAlign: 'center',
        renderCell: ({ value }: GridRenderCellParams<MatchedOffer>) => (
          <Chip
            label={MATCH_TYPE_LABELS[value as string] ?? (value as string)}
            size="small"
            color={MATCH_TYPE_COLORS[value as string] ?? 'default'}
          />
        ),
      },
      {
        field: 'relevanceScore',
        headerName: 'Relevance',
        width: 100,
        align: 'center',
        headerAlign: 'center',
        renderCell: ({ value }: GridRenderCellParams<MatchedOffer>) => (
          <Typography variant="body2">{Math.round(((value as number) ?? 0) * 100)}%</Typography>
        ),
      },
    ],
    [],
  );

  if (isAttractionLoading) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!attraction) {
    return (
      <Container maxWidth="xl">
        <PageHeader title="Attraction Not Found" backButton="/experiences?tab=attractions" />
        <Alert severity="error">Attraction not found</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <PageHeader
        title={attraction.name}
        subtitle={`${attraction.city}, ${attraction.country}`}
        backButton="/experiences?tab=attractions"
        sticky
      />

      {/* Attraction Info + Hero Image */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 8 }}>
          <Paper elevation={1} sx={{ p: 3 }}>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="h6">Details</Typography>
              <Chip
                icon={<AiIcon sx={{ fontSize: 16 }} />}
                label="AI Generated"
                size="small"
                variant="outlined"
                color="primary"
              />
            </Stack>
            <Stack spacing={1.5}>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Description
                </Typography>
                <Typography variant="body2">{attraction.description || 'No description'}</Typography>
              </Box>
              <Stack direction="row" spacing={4}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Type
                  </Typography>
                  <Box>
                    <Chip label={formatAttractionType(attraction.type)} size="small" />
                  </Box>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Popularity Rank
                  </Typography>
                  <Typography variant="body2">#{attraction.popularityRank}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Offers
                  </Typography>
                  <Typography variant="body2">{attraction.offerCount}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Status
                  </Typography>
                  <Box>
                    <Chip
                      label={attraction.active ? 'Active' : 'Inactive'}
                      size="small"
                      color={attraction.active ? 'success' : 'default'}
                    />
                  </Box>
                </Box>
              </Stack>
              <Stack direction="row" spacing={4}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Coordinates
                  </Typography>
                  <Typography variant="body2">
                    {attraction.latitude}, {attraction.longitude}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Attraction ID
                  </Typography>
                  <Typography variant="body2">{attraction.attractionId}</Typography>
                </Box>
                {attraction.lePlaceId && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      LE Place ID
                    </Typography>
                    <Typography variant="body2">{attraction.lePlaceId}</Typography>
                  </Box>
                )}
              </Stack>
            </Stack>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Paper elevation={1} sx={{ p: 3 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="h6">Hero Image</Typography>
              <Button size="small" variant="outlined" onClick={() => setIsImagePickerOpen(true)}>
                Change
              </Button>
            </Stack>
            {attraction.imageUrl ? (
              <Card elevation={0}>
                <CardMedia
                  component="img"
                  image={
                    attraction.imageUrl.startsWith('http')
                      ? attraction.imageUrl
                      : `${window.configs.IMAGE_HOST}/fl_progressive,q_auto:eco,c_fill,g_auto,w_400,h_300/${attraction.imageUrl}`
                  }
                  alt={attraction.name}
                  sx={{ height: 200, objectFit: 'cover', borderRadius: 1 }}
                />
              </Card>
            ) : (
              <Box
                sx={{
                  height: 200,
                  borderRadius: 1,
                  bgcolor: 'grey.100',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  No image set (auto-selected)
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Linked Offers */}
      <Paper elevation={1} sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Linked Offers ({offersTotal})
        </Typography>
        {offersError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Error loading offers: {offersError instanceof Error ? offersError.message : String(offersError)}
          </Alert>
        )}
        <Box sx={{ height: 450 }}>
          <DataGridPro
            rows={offers}
            columns={offerColumns}
            loading={isOffersLoading}
            pagination
            paginationMode="server"
            paginationModel={offersPaginationModel}
            onPaginationModelChange={handleOffersPaginationChange}
            pageSizeOptions={[10, 25, 50]}
            rowCount={offersTotal}
            getRowId={(row) => row.id}
            disableRowSelectionOnClick
            onRowClick={(params) => history.push(`/experience/${params.row.id}`)}
            sx={{
              height: '100%',
              '& .MuiDataGrid-row': { cursor: 'pointer' },
              '& .MuiDataGrid-cell': {
                display: 'flex',
                alignItems: 'center',
              },
              '& .MuiDataGrid-row:hover': {
                bgcolor: alpha(themeColor, 0.08),
              },
            }}
          />
        </Box>
      </Paper>

      {/* Image Picker Dialog */}
      <Dialog open={isImagePickerOpen} onClose={() => setIsImagePickerOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Select Hero Image</DialogTitle>
        <DialogContent>
          {imagesError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Error loading images: {imagesError instanceof Error ? imagesError.message : String(imagesError)}
            </Alert>
          )}
          {isImagesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : images.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
              No images available from linked offers.
            </Typography>
          ) : (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {images.map((image) => (
                <Grid size={{ xs: 6, sm: 4, md: 3 }} key={image.imageId}>
                  <Card
                    sx={{
                      cursor: 'pointer',
                      border: '2px solid transparent',
                      '&:hover': { borderColor: themeColor },
                      position: 'relative',
                    }}
                    onClick={() => handleSetImage(image.imageId, image.url)}
                  >
                    <CardMedia
                      component="img"
                      image={image.url}
                      alt={image.offerTitle}
                      sx={{ height: 120, objectFit: 'cover' }}
                    />
                    <Box sx={{ p: 1 }}>
                      <Typography variant="caption" noWrap>
                        {image.offerTitle}
                      </Typography>
                    </Box>
                    {setImageMutation.isPending && setImageMutation.variables?.imageId === image.imageId && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: 'rgba(255,255,255,0.7)',
                        }}
                      >
                        <CircularProgress size={24} />
                      </Box>
                    )}
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Tooltip title="Reset to auto-selected image">
            <Button startIcon={<RefreshIcon />} onClick={handleResetImage} disabled={resetImageMutation.isPending}>
              Reset to Auto
            </Button>
          </Tooltip>
          <Button onClick={() => setIsImagePickerOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
